// A small accessible "what does this mean?" affordance: an info (ⓘ) icon
// that reveals a styled explanation on hover AND keyboard focus, dismisses
// on Escape/blur, and is tap-toggleable on touch. The popover is rendered
// through a portal with fixed positioning so it is never clipped by the
// `overflow-x-auto` cards or the `overflow-y-auto` rule drawer it lives in.
//
// Copy lives in `@/lib/glossary` — spread an entry in: <InfoTip {...TIPS.mcl} />.

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export interface InfoTipContent {
  /** Name of the thing being explained — used for the accessible label. */
  label: string;
  /** The explanation of what the parameter *means* (not how to set it). */
  body: ReactNode;
  /** Optional regulatory citation, shown muted beneath the body. */
  cite?: string;
}

interface InfoTipProps extends InfoTipContent {
  /** Extra classes for the trigger button (alignment tweaks). */
  className?: string;
}

type Placement = "top" | "bottom";
interface Coords {
  top: number;
  left: number;
  placement: Placement;
}

const OPEN_DELAY = 120;
const CLOSE_DELAY = 100;
const MARGIN = 8;
const GAP = 6;

export function InfoTip({ label, body, cite, className }: InfoTipProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const id = useId();

  const clearTimer = useCallback(() => window.clearTimeout(timer.current), []);
  const openNow = useCallback(() => {
    clearTimer();
    setOpen(true);
  }, [clearTimer]);
  const closeNow = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);
  const scheduleOpen = useCallback(() => {
    clearTimer();
    timer.current = window.setTimeout(() => setOpen(true), OPEN_DELAY);
  }, [clearTimer]);
  const scheduleClose = useCallback(() => {
    clearTimer();
    timer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY);
  }, [clearTimer]);

  // Measure the trigger and the popover, then place it above (preferred) or
  // below, clamped to stay on screen. Runs before paint to avoid a flash.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    const btn = btnRef.current;
    const pop = popRef.current;
    if (!btn || !pop) return;
    const b = btn.getBoundingClientRect();
    const p = pop.getBoundingClientRect();
    const fitsAbove = b.top - p.height - GAP > MARGIN;
    const placement: Placement = fitsAbove ? "top" : "bottom";
    const top = placement === "top" ? b.top - p.height - GAP : b.bottom + GAP;
    const rawLeft = b.left + b.width / 2 - p.width / 2;
    const left = Math.max(MARGIN, Math.min(rawLeft, window.innerWidth - p.width - MARGIN));
    setCoords({ top, left, placement });
  }, [open]);

  // Escape closes (and restores focus); scrolling/resizing dismisses since the
  // popover is positioned once and would otherwise detach from its trigger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        closeNow();
        btnRef.current?.focus();
      }
    };
    const onMove = (): void => closeNow();
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, closeNow]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label={`What is ${label}?`}
        aria-describedby={open ? id : undefined}
        className={`infotip-trigger${className ? ` ${className}` : ""}`}
        onPointerEnter={(e) => {
          if (e.pointerType !== "touch") scheduleOpen();
        }}
        onPointerLeave={(e) => {
          if (e.pointerType !== "touch") scheduleClose();
        }}
        onFocus={openNow}
        onBlur={closeNow}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5">
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.25" />
          <circle cx="8" cy="4.7" r="0.95" fill="currentColor" />
          <rect x="7.2" y="6.7" width="1.6" height="5" rx="0.8" fill="currentColor" />
        </svg>
      </button>
      {open &&
        createPortal(
          <div
            ref={popRef}
            id={id}
            role="tooltip"
            className="infotip-pop"
            style={{
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              opacity: coords ? 1 : 0,
            }}
            onPointerEnter={openNow}
            onPointerLeave={scheduleClose}
          >
            <div>{body}</div>
            {cite && <div className="infotip-cite">{cite}</div>}
          </div>,
          document.body,
        )}
    </>
  );
}
